import React, { useEffect, useMemo, useState } from 'react'
import { Collapse, Input, Select, Switch, Typography } from 'antd'
import PropTypes from 'prop-types'

import { ImageUploader, RichTextEditor } from '~components'
import { MultiSelectDropdown } from '~stories'

import { isFoodAndDrinkSectionVenue } from '../../utils/concessionFoodAndDrinkDefaults'
import {
  CONCESSION_GALLERY_VARIANTS,
  reconcileGalleryItemsForVariant
} from '../../utils/concessionGalleryItemDefaults'
import {
  buildVenuePartnerBenefitsFromDataField,
  getBlankVenuePartnerBenefits
} from '../../utils/concessionPageDefaults'
import { hasConcessionVendorGridSectionFieldErrors, isVendorGridSectionVenue } from '../../utils/concessionVendorGridDefaults'
import { fetchConcessionVendorFilterOptionsByVenueId } from '../../utils/fetchConcessionFiltersByVenueId'
import { fetchVenuePartnerBenefitsByVenueId } from '../../utils/fetchVenuePartnerBenefitsByVenueId'
import { ConcessionGalleryItemsEditor } from '../ConcessionGalleryItems'
import { ConcessionVendorGridEditor } from '../ConcessionVendorGrid'
import styles from '../FAQPageSections/FAQAdminFields.module.css'

const { Panel } = Collapse
const { Text } = Typography
const { TextArea } = Input

const SectionToggle = ({ checked, onChange }) => (
  <div onClick={event => event.stopPropagation()}>
    <Switch checked={checked !== false} onChange={onChange} size="small" />
  </div>
)

SectionToggle.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired
}

const ConcessionPageSections = ({
  page,
  venues = [],
  concessionErrors = {},
  showConcessionValidationErrors = false,
  onHeroBannerChange,
  onFeaturedVendorChange,
  onVendorGridChange,
  onPromotionalVideoChange,
  onMediaCarouselChange,
  onVenuePartnerBenefitsChange,
  onDisclaimerChange,
  onFoodAndDrinkChange
}) => {
  const heroBanner = page?.hero_banner || {}
  const featuredVendorSection = page?.featured_vendor_section || {}
  const vendorGridSection = page?.vendor_grid_section || {}
  const promotionalVideo = page?.promotional_video || {}
  const mediaCarouselFooter = page?.media_only_carousel_footer || {}
  const venuePartnerBenefits = page?.venue_partner_benefits || {}
  const disclaimer = page?.disclaimer || {}
  const foodAndDrink = page?.food_and_drink || {}
  const showFoodAndDrinkSection = isFoodAndDrinkSectionVenue(page?.venue_id, venues)
  const showVendorGridSection = isVendorGridSectionVenue(page?.venue_id, venues)
  const selectedVenueName = useMemo(() => {
    if (!page?.venue_id || !venues?.length) {
      return ''
    }
    const venue = venues.find(v => String(v._id) === String(page.venue_id))
    return venue?.name || ''
  }, [page?.venue_id, venues])
  const [activePanelKey, setActivePanelKey] = useState(undefined)
  const [venuePartnerBenefitsOptions, setVenuePartnerBenefitsOptions] = useState([])
  const [cuisineFilterOptions, setCuisineFilterOptions] = useState([])
  const [cuisineFilterPlaceholder, setCuisineFilterPlaceholder] = useState('Select cuisine')
  const [dietaryFilterOptions, setDietaryFilterOptions] = useState([])
  const [dietaryFilterPlaceholder, setDietaryFilterPlaceholder] = useState('Select dietary preferences')
  const [locationFilterLevels, setLocationFilterLevels] = useState([])
  const [locationFilterPlaceholder, setLocationFilterPlaceholder] = useState('All Levels')
  const selectedVenuePartnerBenefitsId = useMemo(() => (
    venuePartnerBenefits.data_field_id
      ? String(venuePartnerBenefits.data_field_id)
      : undefined
  ), [venuePartnerBenefits.data_field_id])

  useEffect(() => {
    if (!page?.venue_id) {
      setVenuePartnerBenefitsOptions([])
      return
    }

    fetchVenuePartnerBenefitsByVenueId(page.venue_id)
      .then(fields => setVenuePartnerBenefitsOptions(
        (fields || []).map(field => {
          const fieldTitle = field?.title || ''
          const label = selectedVenueName
            ? `${selectedVenueName} — ${fieldTitle}`
            : fieldTitle
          return {
            ...field,
            _id: field?._id != null ? String(field._id) : field?._id,
            title: label
          }
        })
      ))
      .catch(() => setVenuePartnerBenefitsOptions([]))
  }, [page.venue_id, selectedVenueName])

  useEffect(() => {
    if (!page?.venue_id) {
      setCuisineFilterOptions([])
      setCuisineFilterPlaceholder('Select cuisine')
      setDietaryFilterOptions([])
      setDietaryFilterPlaceholder('Select dietary preferences')
      setLocationFilterLevels([])
      setLocationFilterPlaceholder('All Levels')
      return
    }

    fetchConcessionVendorFilterOptionsByVenueId(page.venue_id)
      .then(({ cuisine, dietary, location }) => {
        setCuisineFilterOptions(cuisine?.options || [])
        setCuisineFilterPlaceholder(cuisine?.placeholder || 'Select cuisine')
        setDietaryFilterOptions(dietary?.options || [])
        setDietaryFilterPlaceholder(dietary?.placeholder || 'Select dietary preferences')
        setLocationFilterLevels(location?.levels || [])
        setLocationFilterPlaceholder(location?.placeholder || 'All Levels')
      })
      .catch(() => {
        setCuisineFilterOptions([])
        setCuisineFilterPlaceholder('Select cuisine')
        setDietaryFilterOptions([])
        setDietaryFilterPlaceholder('Select dietary preferences')
        setLocationFilterLevels([])
        setLocationFilterPlaceholder('All Levels')
      })
  }, [page.venue_id])

  useEffect(() => {
    if (
      showConcessionValidationErrors &&
      (concessionErrors.foodAndDrinkTitle || concessionErrors.foodAndDrinkDescription)
    ) {
      setActivePanelKey('food-and-drink')
    }
  }, [
    concessionErrors.foodAndDrinkDescription,
    concessionErrors.foodAndDrinkTitle,
    showConcessionValidationErrors
  ])

  useEffect(() => {
    if (
      showConcessionValidationErrors &&
      hasConcessionVendorGridSectionFieldErrors(concessionErrors.vendorGrid)
    ) {
      setActivePanelKey('vendor-grid')
    }
  }, [concessionErrors.vendorGrid, showConcessionValidationErrors])

  const handleVenuePartnerBenefitsSelect = selectedId => {
    if (!selectedId) {
      onVenuePartnerBenefitsChange(getBlankVenuePartnerBenefits({
        enabled: venuePartnerBenefits.enabled
      }))
      return
    }

    const selectedField = venuePartnerBenefitsOptions.find(
      field => String(field._id) === String(selectedId)
    )

    onVenuePartnerBenefitsChange(
      buildVenuePartnerBenefitsFromDataField(selectedField, {
        enabled: venuePartnerBenefits.enabled
      })
    )
  }

  return (
    <Collapse
      accordion
      activeKey={activePanelKey}
      expandIconPosition="right"
      onChange={setActivePanelKey}
      style={{ marginTop: 16 }}
    >
      <Panel
        key="hero-banner"
        header="Hero"
      >
        <div className={styles.fieldGroup}>
          <Text className={styles.hint} style={{ display: 'block', marginBottom: 16 }}>
            The Banner Section serves as the hero area at the top of the page. Banner title
            and hero image are managed below.
          </Text>
          <div className={styles.fieldRow}>
            <Text className={`${styles.fieldLabel}${concessionErrors.heroBannerTitle ? ` ${styles.fieldLabelError}` : ''}`}>Banner Title</Text>
            <div className={concessionErrors.heroBannerTitle ? styles.fieldControlError : undefined}>
              <Input
                onChange={event => onHeroBannerChange({ title: event.target.value })}
                placeholder="MSG Eats"
                status={concessionErrors.heroBannerTitle ? 'error' : undefined}
                value={heroBanner.title || ''}
              />
            </div>
            {concessionErrors.heroBannerTitle ? (
              <Text className={styles.fieldError}>{concessionErrors.heroBannerTitle}</Text>
            ) : (
              <Text className={styles.hint}>
                Headline text displayed over the hero image.
              </Text>
            )}
          </div>
          <div className={styles.fieldRow}>
            <Text className={`${styles.fieldLabel}${concessionErrors.heroBannerImage ? ` ${styles.fieldLabelError}` : ''}`}>Hero Image</Text>
            <div className={concessionErrors.heroBannerImage ? styles.fieldControlError : undefined}>
              <ImageUploader
                alt={heroBanner.image?.alt || ''}
                handleIconChange={({ alt, url }) => onHeroBannerChange({
                  image: {
                    ...(heroBanner.image || {}),
                    url: url || heroBanner.image?.url || heroBanner.image?.src || '',
                    alt: alt ?? heroBanner.image?.alt ?? ''
                  }
                })}
                handleUpload={url => onHeroBannerChange({
                  image: {
                    ...(heroBanner.image || {}),
                    url,
                    alt: heroBanner.image?.alt || ''
                  }
                })}
                logoUrl={heroBanner.image?.url || heroBanner.image?.src}
                pageType="concession"
              />
            </div>
            {concessionErrors.heroBannerImage ? (
              <Text className={styles.fieldError}>{concessionErrors.heroBannerImage}</Text>
            ) : (
              <Text className={styles.hint}>
                Full-width banner image shown at the top of the concessions page.
              </Text>
            )}
          </div>
          <div className={styles.fieldRow}>
            <Text className={styles.fieldLabel}>Image Alt Text</Text>
            <Input
              onChange={event => onHeroBannerChange({
                image: {
                  ...(heroBanner.image || {}),
                  url: heroBanner.image?.url || heroBanner.image?.src || '',
                  alt: event.target.value
                }
              })}
              placeholder="A stacked cheeseburger from an MSG concession stand"
              value={heroBanner.image?.alt || ''}
            />
          </div>
        </div>
      </Panel>

      <Panel
        key="featured-vendors"
        extra={(
          <SectionToggle
            checked={featuredVendorSection.enabled}
            onChange={enabled => onFeaturedVendorChange({ enabled })}
          />
        )}
        header="Featured Vendor Section"
      >
        <div className={styles.fieldGroup}>
          <div className={styles.fieldRow}>
            <Text className={`${styles.fieldLabel}${concessionErrors.featuredVendor?.title ? ` ${styles.fieldLabelError}` : ''}`}>Section Header</Text>
            <div className={concessionErrors.featuredVendor?.title ? styles.fieldControlError : undefined}>
              <Input
                onChange={event => onFeaturedVendorChange({ title: event.target.value })}
                placeholder="MSG Eats Spotlight"
                status={concessionErrors.featuredVendor?.title ? 'error' : undefined}
                value={featuredVendorSection.title || ''}
              />
            </div>
            {concessionErrors.featuredVendor?.title && (
              <Text className={styles.fieldError}>{concessionErrors.featuredVendor.title}</Text>
            )}
          </div>
          <div className={styles.fieldRow}>
            <Text className={styles.fieldLabel}>Variant</Text>
            <Select
              onChange={type => onFeaturedVendorChange({
                type,
                galleryItems: reconcileGalleryItemsForVariant(
                  featuredVendorSection.galleryItems,
                  type
                )
              })}
              options={[
                { label: 'Spotlight', value: CONCESSION_GALLERY_VARIANTS.SPOTLIGHT },
                { label: 'Full Card Carousel', value: CONCESSION_GALLERY_VARIANTS.FULL_CARD_CAROUSEL },
                { label: 'Media Only Carousel', value: CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL }
              ]}
              style={{ width: '100%' }}
              value={featuredVendorSection.type || CONCESSION_GALLERY_VARIANTS.SPOTLIGHT}
            />
            <Text className={styles.hint}>
              Choose one display variant. Carousel variants support 2-5 cards; Spotlight supports one featured item.
            </Text>
          </div>
          {concessionErrors.featuredVendor?.cardCount && (
            <Text className={styles.fieldError}>{concessionErrors.featuredVendor.cardCount}</Text>
          )}
          <ConcessionGalleryItemsEditor
            galleryItems={featuredVendorSection.galleryItems || []}
            itemErrors={concessionErrors.featuredVendor?.items || []}
            onChange={galleryItems => onFeaturedVendorChange({ galleryItems })}
            variantType={featuredVendorSection.type || CONCESSION_GALLERY_VARIANTS.SPOTLIGHT}
          />
        </div>
      </Panel>

      {showVendorGridSection && (
        <Panel
          key="vendor-grid"
          extra={(
            <SectionToggle
              checked={vendorGridSection.enabled}
              onChange={enabled => onVendorGridChange({ enabled })}
            />
          )}
          header="Vendor Grid"
        >
          <div className={styles.fieldGroup}>
            <div className={styles.fieldRow}>
              <Text className={`${styles.fieldLabel}${concessionErrors.vendorGrid?.title ? ` ${styles.fieldLabelError}` : ''}`}>Section Title</Text>
              <div className={concessionErrors.vendorGrid?.title ? styles.fieldControlError : undefined}>
                <Input
                  onChange={event => onVendorGridChange({ title: event.target.value })}
                  placeholder="MSG Eats"
                  status={concessionErrors.vendorGrid?.title ? 'error' : undefined}
                  value={vendorGridSection.title || ''}
                />
              </div>
              {concessionErrors.vendorGrid?.title && (
                <Text className={styles.fieldError}>{concessionErrors.vendorGrid.title}</Text>
              )}
            </div>
            <div className={styles.fieldRow}>
              <Text className={styles.fieldLabel}>Load More Label</Text>
              <Input
                onChange={event => onVendorGridChange({ load_more_label: event.target.value })}
                placeholder="More Locations"
                value={vendorGridSection.load_more_label || ''}
              />
            </div>
            <div className={styles.fieldRow}>
              <Text className={styles.fieldLabel}>No Results Message</Text>
              <Input
                onChange={event => onVendorGridChange({ no_results_message: event.target.value })}
                placeholder="No results found. Please try different filters."
                value={vendorGridSection.no_results_message || ''}
              />
            </div>
            <ConcessionVendorGridEditor
              cuisineFilterOptions={cuisineFilterOptions}
              cuisinePlaceholder={cuisineFilterPlaceholder}
              dietaryFilterOptions={dietaryFilterOptions}
              dietaryPlaceholder={dietaryFilterPlaceholder}
              hasVenue={Boolean(page?.venue_id)}
              itemErrors={concessionErrors.vendorGrid?.vendors || []}
              locationFilterLevels={locationFilterLevels}
              locationPlaceholder={locationFilterPlaceholder}
              onChange={vendors => onVendorGridChange({ vendors })}
              vendors={vendorGridSection.vendors || []}
            />
          </div>
        </Panel>
      )}

      <Panel
        key="promotional-video"
        extra={(
          <SectionToggle
            checked={promotionalVideo.enabled}
            onChange={enabled => onPromotionalVideoChange({ enabled })}
          />
        )}
        header="Promotional Video"
      >
        <div className={styles.fieldGroup}>
          <div className={styles.fieldRow}>
            <Text className={styles.fieldLabel}>Video URL</Text>
            <Text type="secondary">
              Use a YouTube embed URL. Example: https://www.youtube.com/embed/LXb3EKWsInQ?si=SaSQQU5f0W-MFqc1
            </Text>
            <Input
              onChange={event => onPromotionalVideoChange({
                video: {
                  ...(promotionalVideo.video || {}),
                  url: event.target.value
                }
              })}
              placeholder="https://www.youtube.com/embed/LXb3EKWsInQ?si=SaSQQU5f0W-MFqc1"
              style={{ marginTop: 8 }}
              value={promotionalVideo.video?.url || ''}
            />
          </div>
          <div className={styles.fieldRow}>
            <Text className={styles.fieldLabel}>Description</Text>
            <TextArea
              onChange={event => onPromotionalVideoChange({ description: event.target.value })}
              rows={4}
              value={promotionalVideo.description || ''}
            />
          </div>
        </div>
      </Panel>

      <Panel
        key="media-carousel-footer"
        extra={(
          <SectionToggle
            checked={mediaCarouselFooter.enabled}
            onChange={enabled => onMediaCarouselChange({ enabled })}
          />
        )}
        header="Media Only Carousel Footer"
      >
        <div className={styles.fieldGroup}>
          <Text className={styles.hint} style={{ display: 'block', marginBottom: 16 }}>
            Optional footer carousel outside the Featured Vendor section. Configure 2-5 media cards with image or YouTube video.
          </Text>
          {concessionErrors.mediaCarouselFooter?.cardCount && (
            <Text className={styles.fieldError}>{concessionErrors.mediaCarouselFooter.cardCount}</Text>
          )}
          <ConcessionGalleryItemsEditor
            galleryItems={mediaCarouselFooter.galleryItems || []}
            itemErrors={concessionErrors.mediaCarouselFooter?.items || []}
            onChange={galleryItems => onMediaCarouselChange({ galleryItems })}
            variantType={CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL}
          />
        </div>
      </Panel>

      <Panel
        key="venue-partner-benefits"
        extra={(
          <SectionToggle
            checked={venuePartnerBenefits.enabled}
            onChange={enabled => onVenuePartnerBenefitsChange({ enabled })}
          />
        )}
        header="Venue Partner Benefits"
      >
        <div className={styles.fieldGroup}>
          <Text type="secondary">
            {page?.venue_id
              ? `Select a data field to populate partner benefits${selectedVenueName ? ` for ${selectedVenueName}` : ''}`
              : 'Select a page venue above to load partner benefits'}
          </Text>
          <div className={styles.fieldRow}>
            <MultiSelectDropdown
              key={`venue-partner-benefits-${page?.venue_id || 'none'}-${venuePartnerBenefitsOptions.length}-${selectedVenuePartnerBenefitsId || 'none'}`}
              allowClear={true}
              displayField="title"
              dropdownOptions={page?.venue_id ? venuePartnerBenefitsOptions : []}
              handleChange={handleVenuePartnerBenefitsSelect}
              selectedItems={selectedVenuePartnerBenefitsId}
              valueField="_id"
            />
          </div>
          {selectedVenuePartnerBenefitsId && (venuePartnerBenefits.partners_flex_modules || []).length > 0 && (
            <Text type="secondary">
              {`${venuePartnerBenefits.partners_flex_modules.length} benefit module(s) configured.`}
            </Text>
          )}
        </div>
      </Panel>

      <Panel
        key="disclaimer"
        extra={(
          <SectionToggle
            checked={disclaimer.enabled}
            onChange={enabled => onDisclaimerChange({ enabled })}
          />
        )}
        header="Disclaimer"
      >
        <div className={styles.fieldGroup}>
          <div className={styles.fieldRow}>
            <Text className={`${styles.fieldLabel}${concessionErrors.disclaimerTitle ? ` ${styles.fieldLabelError}` : ''}`}>Section Header</Text>
            <div className={concessionErrors.disclaimerTitle ? styles.fieldControlError : undefined}>
              <Input
                onChange={event => onDisclaimerChange({ title: event.target.value })}
                placeholder="Alcohol Serving and Consumption"
                status={concessionErrors.disclaimerTitle ? 'error' : undefined}
                value={disclaimer.title || ''}
              />
            </div>
            {concessionErrors.disclaimerTitle && (
              <Text className={styles.fieldError}>{concessionErrors.disclaimerTitle}</Text>
            )}
          </div>
          <div className={styles.fieldRow}>
            <Text className={`${styles.fieldLabel}${concessionErrors.disclaimerDescription ? ` ${styles.fieldLabelError}` : ''}`}>Description</Text>
            <div className={concessionErrors.disclaimerDescription ? styles.fieldControlErrorRichText : undefined}>
              <RichTextEditor
                minHeight={160}
                onChange={html => onDisclaimerChange({ html_description: html })}
                placeholder="Write disclaimer content, including bullet lists if needed…"
                value={disclaimer.html_description || ''}
              />
            </div>
            {concessionErrors.disclaimerDescription && (
              <Text className={styles.fieldError}>{concessionErrors.disclaimerDescription}</Text>
            )}
          </div>
        </div>
      </Panel>

      {showFoodAndDrinkSection && (
        <Panel
          key="food-and-drink"
          extra={(
            <SectionToggle
              checked={foodAndDrink.enabled}
              onChange={enabled => onFoodAndDrinkChange({ enabled })}
            />
          )}
          header="Food & Drink"
        >
          <div className={styles.fieldGroup}>
            <Text className={styles.hint} style={{ display: 'block', marginBottom: 16 }}>
              Available for theater venues only. Use the description editor for copy, bullet lists, and links.
            </Text>
            <div className={styles.fieldRow}>
              <Text className={`${styles.fieldLabel}${concessionErrors.foodAndDrinkTitle ? ` ${styles.fieldLabelError}` : ''}`}>Section Header</Text>
              <div className={concessionErrors.foodAndDrinkTitle ? styles.fieldControlError : undefined}>
                <Input
                  onChange={event => onFoodAndDrinkChange({ title: event.target.value })}
                  placeholder="Food & Drink"
                  status={concessionErrors.foodAndDrinkTitle ? 'error' : undefined}
                  value={foodAndDrink.title || ''}
                />
              </div>
              {concessionErrors.foodAndDrinkTitle && (
                <Text className={styles.fieldError}>{concessionErrors.foodAndDrinkTitle}</Text>
              )}
            </div>
            <div className={styles.fieldRow}>
              <Text className={`${styles.fieldLabel}${concessionErrors.foodAndDrinkDescription ? ` ${styles.fieldLabelError}` : ''}`}>Description</Text>
              <div className={concessionErrors.foodAndDrinkDescription ? styles.fieldControlErrorRichText : undefined}>
                <RichTextEditor
                  minHeight={160}
                  onChange={html => onFoodAndDrinkChange({ html_description: html })}
                  placeholder="Write food & drink content, including bullet lists and links if needed…"
                  value={foodAndDrink.html_description || ''}
                />
              </div>
              {concessionErrors.foodAndDrinkDescription && (
                <Text className={styles.fieldError}>{concessionErrors.foodAndDrinkDescription}</Text>
              )}
            </div>
          </div>
        </Panel>
      )}
    </Collapse>
  )
}

ConcessionPageSections.propTypes = {
  concessionErrors: PropTypes.object,
  onDisclaimerChange: PropTypes.func.isRequired,
  onFeaturedVendorChange: PropTypes.func.isRequired,
  onFoodAndDrinkChange: PropTypes.func.isRequired,
  onHeroBannerChange: PropTypes.func.isRequired,
  onMediaCarouselChange: PropTypes.func.isRequired,
  onPromotionalVideoChange: PropTypes.func.isRequired,
  onVendorGridChange: PropTypes.func.isRequired,
  onVenuePartnerBenefitsChange: PropTypes.func.isRequired,
  page: PropTypes.object,
  showConcessionValidationErrors: PropTypes.bool,
  venues: PropTypes.array
}

export default ConcessionPageSections
